import React, { useCallback, useMemo, useState } from 'react';
import { Select, Spin } from 'antd';
import { SelectValue } from 'antd/lib/select';
import debounce from 'lodash/debounce';
import { useTranslation } from 'react-i18next';
import { searchZones } from '../../../service/VolunteerApplicationService';
import { displayError } from '../../../shared/Utils';
import { useToastMessage } from '../../../hooks/useToastMessage';

const { Option } = Select;

interface OptionData {
	value: string;
}

interface RemoteSingleSelectProps {
	onChange?: (values: OptionData) => void;
	value?: OptionData;
	functionarySrcmId?: string;
	regionId?: string;
	isFetchedDataBySRCMID?: boolean;
}

const RemoteSingleSelect: React.FC<RemoteSingleSelectProps> = ({
	regionId,
	onChange,
	value,
	functionarySrcmId,
	isFetchedDataBySRCMID,
}: RemoteSingleSelectProps) => {
	const { t } = useTranslation();
	const { showToastMessage } = useToastMessage();
	const [options, setOptions] = useState<OptionData[]>([]);
	const [loading, setLoading] = useState(false);

	const fetchData = useCallback(
		async (searchValue: string) => {
			setLoading(true);

			if (functionarySrcmId && regionId) {
				try {
					const zonesPage = await searchZones({
						functionarySrcmId,
						pageIndex: 0,
						pageSize: 100,
						searchKey: searchValue,
						includeOnlyZonesOfApprover: true,
						displayAllZones: true,
						isPaginationRequired: searchValue ? true : false,
						regionIds: [Number(regionId)],
					});
					if (zonesPage.zones) {
						const transformedOptions: OptionData[] = zonesPage.zones.map(
							(item: string) => ({
								value: item,
								label: item,
							})
						);

						setOptions(transformedOptions);
					}
				} catch (err) {
					const errorMsg = displayError(err);
					showToastMessage('error', errorMsg);
				}
			} else {
				setOptions([]);
			}

			setLoading(false);
		},
		[functionarySrcmId, regionId, showToastMessage]
	);

	const debouncedFetchData = useMemo(
		() => debounce(fetchData, 500),
		[fetchData]
	);

	const handleSearch = useCallback(
		(searchKey: SelectValue) => {
			void debouncedFetchData(searchKey as string);
		},
		[debouncedFetchData]
	);

	const handleFocus = useCallback(() => {
		if (options.length === 0) {
			void fetchData('');
		}
	}, [fetchData, options.length]);

	return (
		<Select
			showSearch
			defaultActiveFirstOption={false}
			filterOption={false}
			placeholder={t('searchZones')}
			notFoundContent={loading ? <Spin size="small" /> : null}
			onFocus={handleFocus}
			onSearch={handleSearch}
			onChange={onChange}
			labelInValue
			value={value}
			suffixIcon={null}
			className="font-sans text-sm text-secondary-gray"
			size="large"
			disabled={!!regionId && isFetchedDataBySRCMID}
		>
			{options.map((option) => (
				<Option key={option.value} value={option.value}>
					{option.value}
				</Option>
			))}
		</Select>
	);
};

export default RemoteSingleSelect;
